import React, { useEffect, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/SidebarWithSearch.css"; 
import UserDetailsModal from "./UserDetailsModal";
import UserSearchAutocomplete from "./UserSearchAutocomplete";
import UserList from "./UserList";
import CustomSpinner from "./CustomSpinner";

// Map user object into UI-friendly shape
const mapUserData = (user, channel = null, chatClient) => {
  const lastMessage = channel?.state.messages?.slice(-1)[0];

  return {
    id: user.id,
    name: user.name,
    firstName: user.firstName || user.name.split(" ")[0],
    lastName: user.lastName || user.name.split(" ")[1] || "",
    middleName: user.middleName || "",
    role: user.role,
    photo: user.photo,
    organization: user.organization || "N/A",
    clientName: user.eventClientName || "N/A",
    eventName: user.eventName || "N/A",
    team: chatClient?.user?.teams?.[0] || "",
    online: user.online,
    lastMessageDate: lastMessage?.created_at || null,
    lastMessageText: lastMessage?.text || "",
    unreadCount: channel?.state.unreadCount || 0,
  };
};

const SidebarWithSearch = ({
  chatClient,
  onUserSelect,
  currentUserId,
  chatUserId,
}) => {
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeUserId, setActiveUserId] = useState(chatUserId);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true); // State for sidebar visibility
     const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };
  const fetchUsersFromChannels = useCallback(() => {
    const messageChannels = Object.values(chatClient.activeChannels).filter(
      (channel) => channel.type === "messaging"
    );

    const users = messageChannels.flatMap((channel) =>
      Object.values(channel.state.members)
        .filter(
          (member) =>
            member.user.id !== currentUserId && member.user.share === true
        )
        .map((member) => mapUserData(member.user, channel, chatClient))
    );

    const uniqueUsers = Array.from(
      new Map(users.map((u) => [u.id, u])).values()
    );
    uniqueUsers.sort((a, b) => a.name.localeCompare(b.name));
    setAllUsers(uniqueUsers);

    const selected = uniqueUsers.find((u) => u.id === activeUserId);
    const rest = uniqueUsers.filter((u) => u.id !== activeUserId);
    setFilteredUsers(selected ? [selected, ...rest] : rest);
  }, [chatClient, currentUserId, activeUserId]);

  // Wait for Stream Chat connection and fetch users
  useEffect(() => {
    let timeoutId;

    const handleConnected = () => {
      timeoutId = setTimeout(() => {
        fetchUsersFromChannels();
        setIsLoading(false);
      }, 300); // Small delay to ensure `activeChannels` is populated
    };

    if (chatClient._hasConnectionID()) {
      handleConnected();
    } else {
      chatClient.on("connection.changed", () => {
        if (chatClient._hasConnectionID()) {
          handleConnected();
        }
      });
    }

    return () => clearTimeout(timeoutId);
  }, [chatClient, fetchUsersFromChannels]);

  // Listen for live events and update user state
  useEffect(() => {
    const updateUserFromChannel = (channel) => {
      const updatedUsers = Object.values(channel.state.members)
        .filter((m) => m.user.id !== currentUserId)
        .map((m) => mapUserData(m.user, channel, chatClient));

      setAllUsers((prev) => {
        const map = new Map(prev.map((u) => [u.id, u]));
        updatedUsers.forEach((u) => map.set(u.id, u));
        return Array.from(map.values());
      });

      setFilteredUsers((prev) => {
        const ids = new Set(updatedUsers.map((u) => u.id));
        const others = prev.filter((u) => !ids.has(u.id));
        return [...updatedUsers, ...others];
      });
    };

    chatClient.on("channel.created.custom", (e) =>
      updateUserFromChannel(e.channel)
    );
    chatClient.on("message.new", (e) => {
      const channel = chatClient.channel(e.channel_type, e.channel_id);
      updateUserFromChannel(channel, e.message);
    });
    chatClient.on("channel.updated", (e) => {
      const channel = chatClient.channel(e.channel_type, e.channel_id);
      updateUserFromChannel(channel,e.message)});
    chatClient.on("channel.hidden", (e) => {
      const channel = chatClient.channel(e.channel_type, e.channel_id);
      updateUserFromChannel(channel,e.message)});

    return () => {
      chatClient.off("channel.created.custom");
      chatClient.off("message.new");
      chatClient.off("channel.updated");
      chatClient.off("channel.hidden");
    };
  }, [chatClient, currentUserId]);

  const handleUserSelect = useCallback((userId) => {
    setActiveUserId(userId);
    onUserSelect(userId);
    const selectedUser = allUsers.find((u) => u.id === userId);
    if (!selectedUser) return;

    const others = allUsers.filter((u) => u.id !== userId);
    setFilteredUsers([selectedUser, ...others]);
  }, [onUserSelect, allUsers]);

  const handleAutocompleteUserSelect = useCallback(
    (user) => {
      const mapped = mapUserData(user, null, chatClient);
      const exists = allUsers.some((u) => u.id === mapped.id);

      if (!exists) {
        setAllUsers((prev) => [...prev, mapped]);
      }

      const others = filteredUsers.filter((u) => u.id !== mapped.id);
      setFilteredUsers([mapped, ...others]);
      setActiveUserId(mapped.id);
      onUserSelect(mapped.id);
    },
    [allUsers, filteredUsers, chatClient, onUserSelect]
  );

  const handleInfoClick = (user) => {
    setSelectedUser(user);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setSelectedUser(null);
    setIsPopupOpen(false);
  };

  if (isLoading) {
    return <CustomSpinner message="Loading users, please wait..." />;
  }

  return (
      <>
      <button className="hamburger-icon" onClick={toggleSidebar}>
        ☰
      </button>
    <div className={`sidebar ${isSidebarOpen ? "open" : "closed"}`}>
      {isLoading && <CustomSpinner message="Loading users, please wait..." />}
   
      <UserSearchAutocomplete
        chatClient={chatClient}
        currentUserId={currentUserId}
        onUserSelect={handleAutocompleteUserSelect}
      />
      <UserList
        users={filteredUsers}
        activeUserId={activeUserId}
        onUserSelect={handleUserSelect}
        onUserInfoClick={handleInfoClick}
      />
      <UserDetailsModal
        isOpen={isPopupOpen}
        toggle={closePopup}
        user={selectedUser}
      />
      <ToastContainer />
    </div>
    </>
  );
};

export default SidebarWithSearch;
